import { Injectable, NgModule } from '@angular/core';
import {
    DateAdapter,
    MAT_NATIVE_DATE_FORMATS,
    MatDateFormats,
    NativeDateAdapter,
    provideNativeDateAdapter
} from '@angular/material/core';

/**
 * The application's one date / timestamp format.
 *
 * Templates use the DatePipe with APP_DATE_FORMAT / APP_DATETIME_FORMAT
 * ('dd MMM yyyy' / 'dd MMM yyyy, HH:mm'); code that builds strings itself
 * (exports, PDFs, tables filled in TypeScript) uses formatAppDate /
 * formatAppDateTime, which give the same text: "27 Oct 2026, 16:05"; date
 * pickers get it through AppDateModule (below).
 *
 * Built by hand rather than with toLocaleString so the output does not depend
 * on the browser's locale (en-GB now abbreviates September as "Sept").
 */

export const APP_DATE_FORMAT = 'dd MMM yyyy';
export const APP_DATETIME_FORMAT = 'dd MMM yyyy, HH:mm';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad = (n: number) => String(n).padStart(2, '0');

function toDate(value: Date | string | number | null | undefined): Date | null {
    if (value === null || value === undefined || value === '') return null;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? null : d;
}

/** "27 Oct 2026"; '' for a missing or invalid value. */
export function formatAppDate(value: Date | string | number | null | undefined): string {
    const d = toDate(value);
    if (!d) return '';
    return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * "27 Oct 2026, 16:05" ("27 Oct 2026, 16:05:09" with seconds, for audit
 * records); '' for a missing or invalid value.
 */
export function formatAppDateTime(value: Date | string | number | null | undefined, withSeconds = false): string {
    const d = toDate(value);
    if (!d) return '';
    const time = `${pad(d.getHours())}:${pad(d.getMinutes())}` + (withSeconds ? `:${pad(d.getSeconds())}` : '');
    return `${formatAppDate(d)}, ${time}`;
}

// ── Date pickers ─────────────────────────────────────────────

/** Marker display format: date picker inputs show the app's date format. */
const APP_DATE_INPUT = 'app-date-input';

/**
 * Native date adapter whose text matches formatAppDate ("28 Oct 2026")
 * instead of the browser locale's "10/28/2026". Typed values may be
 * "28 Oct 2026", "28/10/2026" (day first) or ISO "2026-10-28".
 */
@Injectable()
export class AppDateAdapter extends NativeDateAdapter {
    override format(date: Date, displayFormat: object | string): string {
        if (displayFormat === APP_DATE_INPUT) {
            return formatAppDate(date);
        }
        return super.format(date, displayFormat as object);
    }

    override parse(value: any, parseFormat?: any): Date | null {
        if (typeof value === 'string') {
            const dmy = value.trim().match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{4})$/);
            if (dmy) {
                const date = new Date(+dmy[3], +dmy[2] - 1, +dmy[1]);
                return date.getMonth() === +dmy[2] - 1 ? date : this.invalid();
            }
        }
        return super.parse(value, parseFormat);
    }
}

export const APP_MAT_DATE_FORMATS: MatDateFormats = {
    parse: { ...MAT_NATIVE_DATE_FORMATS.parse, dateInput: APP_DATE_INPUT },
    display: { ...MAT_NATIVE_DATE_FORMATS.display, dateInput: APP_DATE_INPUT }
};

/**
 * Drop-in replacement for MatNativeDateModule: same native Date values, with
 * the app's date format in every date picker input.
 */
@NgModule({
    providers: [
        provideNativeDateAdapter(APP_MAT_DATE_FORMATS),
        { provide: DateAdapter, useClass: AppDateAdapter }
    ]
})
export class AppDateModule {}
